import type { BasicAttackCommand, InputAction, InputCommand, NetObjectId, ObjectSnapshot, PlayerSnapshot, PresentationEvent, ReplicationDelta, ReplicationSnapshot } from './protocol.js';
import { CLIENT_BASELINE_RETENTION_TICKS, applyObjectUpdate } from './protocol.js';
import { sweepMovement, type Terrain } from '../terrain.js';
import { resolveDashMovement } from '../sim/dash-movement.js';
import { COMMON_DASH_MAX_DISTANCE_M } from '../sim/common-dash.js';
import { PLAYER_RADIUS_M } from '../sim/positioning.js';

export interface PredictionOptions {
  readonly terrain?: Terrain;
  readonly actorRadiusM?: number;
}

type PendingDash = { readonly distanceM?: number };
interface PredictedInput { sequence: number; clientTick: number; moveX: number; moveY: number; dash?: PendingDash; }
interface TimedSample { readonly object: ObjectSnapshot; readonly tick: number; readonly skillDisplaced?: boolean; }
interface Position { readonly x: number; readonly y: number; }
interface PresentationOffset { readonly offset: Position; readonly startedAtMs: number; }
interface TimedObject { previous?: ObjectSnapshot; current: ObjectSnapshot; previousTick: number; currentTick: number; samples: TimedSample[]; launch?: PresentationOffset; motionCorrection?: PresentationOffset; }
/** `dashMs` marks a predicted common dash: the drawn position eases from `from` to `to` over that long instead of following one tick. */
interface LocalPresentation { readonly from: Position; readonly to: Position; readonly startedAtMs: number; readonly dashMs?: number; }

/**
 * PROVISIONAL presentation only: how long the local player's drawn position takes to cover a full
 * 10 m common dash, speeding up and then slowing down. The predicted and authoritative position
 * still lands in the dash's own tick.
 */
const DASH_PRESENTATION_MS = 200;
const DASH_PRESENTATION_MIN_MS = 80;

/**
 * PROVISIONAL presentation only: how long the local player's drawn position takes to ease to where a skill's dash put it
 * (`skill_displacement`). The authoritative and predicted position is the landing from that tick on.
 */
export const SKILL_DISPLACEMENT_EASE_MS = 120;
/** PROVISIONAL: spread remote displacement over 18 frames at 60 fps. */
export const REMOTE_SKILL_DISPLACEMENT_EASE_MS = 300;

// A stalled connection must not extrapolate projectiles indefinitely past their authoritative lifetime.
const MAX_ATTACK_EXTRAPOLATION_TICKS = 4;

function cloneObject<T extends ObjectSnapshot>(value: T): T { return structuredClone(value); }
function lerpPosition(from: Position, to: Position, alpha: number): Position {
  return { x: from.x + (to.x - from.x) * alpha, y: from.y + (to.y - from.y) * alpha };
}

/** Transport-independent client model: prediction for self, interpolation for everyone else. */
export class PredictedClient {
  private readonly objects = new Map<NetObjectId, TimedObject>();
  private readonly baselines = new Map<number, Map<NetObjectId, ObjectSnapshot>>();
  /** The one baseline the server is currently naming; never window-pruned. See protocol.ts. */
  private pinnedBaselineTick: number | null = null;
  private readonly inputHistory: PredictedInput[] = [];
  private sequence = 0;
  private clientTick = 0;
  private latestServerTick = -1;
  private latestReceivedAtMs = 0;
  private predicted?: PlayerSnapshot;
  private localPresentation?: LocalPresentation;
  private readonly presentationEvents: PresentationEvent[] = [];
  /** The newest skill displacement of the local player already accepted, by `(tick, seq)`. */
  private lastSkillDisplacement?: { readonly tick: number; readonly seq: number };
  private readonly terrain?: Terrain;
  private readonly actorRadiusM: number;

  /**
   * `tickRate` and `playerSpeedMps` are required and must be the room's own
   * values — take them from `ConnectResult`. They used to default to a third
   * hand-synchronised copy of the world config's numbers, which silently
   * desynchronises prediction the moment a room is configured differently.
   */
  constructor(
    readonly playerNetId: NetObjectId,
    readonly tickRate: number,
    readonly playerSpeedMps: number,
    readonly interpolationDelayTicks = 2,
    prediction?: PredictionOptions,
    /** Monsters are sent less often than players, so they are drawn further behind; defaults to the same delay as everything else. */
    readonly monsterInterpolationDelayTicks = interpolationDelayTicks,
  ) {
    this.terrain = prediction?.terrain;
    this.actorRadiusM = prediction?.actorRadiusM ?? PLAYER_RADIUS_M;
    if (!Number.isFinite(this.actorRadiusM) || this.actorRadiusM < 0) throw new Error('prediction actor radius must be finite and non-negative');
  }

  command(estimatedServerTick: number, moveX: number, moveY: number, aimAngle: number, actions: InputAction[] = [], basicAttackCommand?: BasicAttackCommand, autoSkillSlots: number[] = []): InputCommand {
    const length = Math.hypot(moveX, moveY); if (length > 1) { moveX /= length; moveY /= length; }
    const sequence = ++this.sequence; const clientTick = ++this.clientTick;
    const dash = actions.find((action): action is Extract<InputAction, { type: 'dash' }> => action.type === 'dash');
    const earlierDashPending = this.inputHistory.some((input) => input.dash !== undefined);
    this.inputHistory.push({ sequence, clientTick, moveX, moveY, ...(dash === undefined ? {} : { dash: dash.distanceM === undefined ? {} : { distanceM: dash.distanceM } }) });
    const now = performance.now();
    const displayedBeforePrediction = this.localPosition(now);
    this.updateLocalPresentation(displayedBeforePrediction, now, this.predictStep(moveX, moveY, dash, earlierDashPending));
    return { playerNetId: this.playerNetId, sequence, clientTick, durationTicks: 1, estimatedServerTick, moveX, moveY, aimAngle, actions,
      ...(basicAttackCommand === undefined ? {} : { basicAttackCommand }), ...(autoSkillSlots.length === 0 ? {} : { autoSkillSlots }) };
  }

  receiveSnapshot(snapshot: ReplicationSnapshot): boolean {
    if (snapshot.serverTick <= this.latestServerTick) return false;
    const now = performance.now();
    const previousAttacks = this.captureAttackPositions(now);
    this.latestServerTick = snapshot.serverTick;
    this.latestReceivedAtMs = now;
    const incoming = new Set(snapshot.objects.map((object) => object.netId));
    for (const id of this.objects.keys()) if (!incoming.has(id)) this.objects.delete(id);
    for (const object of snapshot.objects) {
      const current = this.objects.get(object.netId)?.current;
      if (!current || JSON.stringify(current) !== JSON.stringify(object)) this.acceptObject(object, snapshot.serverTick);
    }
    this.storeBaseline(snapshot.serverTick, new Map(snapshot.objects.map((object) => [object.netId, cloneObject(object)])));
    const displayedBeforeReconcile = this.localPosition(this.latestReceivedAtMs);
    this.updateLocalPresentation(displayedBeforeReconcile, this.latestReceivedAtMs, this.reconcile(snapshot.lastProcessedInputSequence));
    this.smoothAttackCorrections(previousAttacks, now);
    // A snapshot is the welcome/reconnect path: the server already processed
    // `lastProcessedInputSequence` commands under this session, so a fresh
    // client model (sequence/clientTick starting at 0) must not resend from
    // scratch and get rejected as stale — see NET-A1. sequence and clientTick
    // are always advanced together by command() (never independently), so the
    // same restored value is correct for both.
    this.sequence = Math.max(this.sequence, snapshot.lastProcessedInputSequence);
    this.clientTick = Math.max(this.clientTick, snapshot.lastProcessedInputSequence);
    return true;
  }

  receiveDelta(delta: ReplicationDelta): boolean {
    if (delta.serverTick <= this.latestServerTick) return false; // old movement can never rewind a reused or live object
    const base = delta.baselineTick === null ? new Map<NetObjectId, ObjectSnapshot>() : this.baselines.get(delta.baselineTick);
    if (!base) return false; // Never apply a delta to a state other than the baseline named on the wire.
    const now = performance.now();
    const previousAttacks = this.captureAttackPositions(now);
    const newlySeenLocalShots = delta.spawns.filter((object) => object.kind === 'attack' && object.ownerNetId === this.playerNetId
      && (object.vx !== 0 || object.vy !== 0) && !this.objects.has(object.netId));
    const reconstructed = new Map([...base].map(([id, object]) => [id, cloneObject(object)]));
    for (const id of delta.despawns) reconstructed.delete(id);
    for (const object of delta.spawns) reconstructed.set(object.netId, cloneObject(object));
    for (const update of [...delta.importantUpdates, ...delta.updates]) reconstructed.set(update.netId, structuredClone(applyObjectUpdate(reconstructed.get(update.netId), update)));
    this.latestServerTick = delta.serverTick;
    this.latestReceivedAtMs = now;
    for (const id of this.objects.keys()) if (!reconstructed.has(id)) this.objects.delete(id);
    const displacedPlayers = new Set(delta.events.filter((event) => event.type === 'skill_displacement').map((event) => event.sourceId));
    for (const object of reconstructed.values()) {
      const current = this.objects.get(object.netId)?.current;
      if (!current || JSON.stringify(current) !== JSON.stringify(object)) this.acceptObject(object, delta.serverTick, displacedPlayers.has(object.netId));
    }
    // Pin the baseline this delta was built from: the server will keep naming
    // it until it hears an ACK that moves its own anchor, however long that
    // takes, so it must survive the rolling window regardless of age.
    this.pinnedBaselineTick = delta.baselineTick;
    this.storeBaseline(delta.serverTick, reconstructed);
    const displayedBeforeReconcile = this.localPosition(this.latestReceivedAtMs);
    const skillDisplaced = this.acceptSkillDisplacements(delta.events);
    this.updateLocalPresentation(displayedBeforeReconcile, this.latestReceivedAtMs, this.reconcile(delta.lastProcessedInputSequence), skillDisplaced);
    // Server launches before applying same-tick movement, whereas the owner
    // snapshot is post-move. Present a NEW local shot at the player's visible
    // position and fade only that visual offset over one tick. Never shift the
    // authoritative snapshot, velocity, collision, or explicit-tick replay.
    const origin = this.localPosition(this.latestReceivedAtMs);
    if (origin) for (const shot of newlySeenLocalShots) {
      const timed = this.objects.get(shot.netId);
      if (timed?.current.kind !== 'attack') continue;
      const dx = origin.x - timed.current.x; const dy = origin.y - timed.current.y;
      const distance = Math.hypot(dx, dy);
      const maxOffset = this.actorRadiusM + this.playerSpeedMps / this.tickRate;
      const scale = distance > maxOffset && distance > 0 ? maxOffset / distance : 1;
      timed.launch = { offset: { x: dx * scale, y: dy * scale }, startedAtMs: this.latestReceivedAtMs };
    }
    this.smoothAttackCorrections(previousAttacks, now);
    // Object state is accepted before presentation, so event consumers can always
    // resolve the referenced actor from the current client model.
    this.presentationEvents.push(...delta.events.map((event) => structuredClone(event)));
    return true;
  }

  localPlayer(): PlayerSnapshot | undefined { return this.predicted && cloneObject(this.predicted); }
  /** The newest server tick this client has applied; status expiry ticks are read against it. */
  get serverTick(): number { return this.latestServerTick; }

  /** Presentation is intentionally lossy; persistent object state is the recovery path. */
  drainPresentationEvents(): PresentationEvent[] { return this.presentationEvents.splice(0).map((event) => structuredClone(event)); }

  /** Current visual position of a moving attack; never changes authoritative state. */
  private attackPosition(timed: TimedObject, now: number): Position {
    const tickDurationMs = 1_000 / this.tickRate;
    const elapsedTicks = Math.min(MAX_ATTACK_EXTRAPOLATION_TICKS, Math.max(0, (now - this.latestReceivedAtMs) / tickDurationMs));
    const elapsedS = Math.max(0, this.latestServerTick + elapsedTicks - timed.currentTick) / this.tickRate;
    const orbit = timed.current.kind === 'attack' ? this.orbitPosition(timed.current, elapsedS, now) : undefined;
    if (orbit) return orbit;
    const blend = (offset?: PresentationOffset) => offset ? Math.max(0, 1 - (now - offset.startedAtMs) / tickDurationMs) : 0;
    return {
      x: timed.current.x + ('vx' in timed.current ? timed.current.vx : 0) * elapsedS
        + (timed.launch?.offset.x ?? 0) * blend(timed.launch)
        + (timed.motionCorrection?.offset.x ?? 0) * blend(timed.motionCorrection),
      y: timed.current.y + ('vy' in timed.current ? timed.current.vy : 0) * elapsedS
        + (timed.launch?.offset.y ?? 0) * blend(timed.launch)
        + (timed.motionCorrection?.offset.y ?? 0) * blend(timed.motionCorrection),
    };
  }

  /**
   * An orb is drawn on its circle, not along its momentary velocity: the velocity is the tangent, so it
   * gives the orb's angle exactly, and its speed over the radius is the turn rate. The circle sits on
   * the drawn owner (or the orb's fixed centre), so an orb never lags or leads the body it circles.
   */
  private orbitPosition(attack: Extract<ObjectSnapshot, { kind: 'attack' }>, elapsedS: number, now: number): Position | undefined {
    const speed = Math.hypot(attack.vx, attack.vy);
    if (attack.presentationKind !== 'ORBIT' || !attack.orbitRadiusM || speed === 0) return undefined;
    const owner = this.objects.get(attack.ownerNetId);
    const sampled = attack.orbitCenter ?? owner?.current;
    // A remote owner is drawn behind its newest state by the interpolation delay; its orbs ride on where it is drawn.
    const tickDurationMs = 1_000 / this.tickRate;
    const elapsedTicks = Math.min(1, Math.max(0, (now - this.latestReceivedAtMs) / tickDurationMs));
    const presentationTick = this.latestServerTick - (owner?.current.kind === 'monster' ? this.monsterInterpolationDelayTicks : this.interpolationDelayTicks) + elapsedTicks;
    const centre = attack.orbitCenter ?? (attack.ownerNetId === this.playerNetId ? this.localPosition(now) : owner && this.delayedPosition(owner, presentationTick, true));
    if (!sampled || !centre) return undefined;
    // Counter-clockwise when the velocity turns left of the radius.
    const turn = (attack.x - sampled.x) * attack.vy - (attack.y - sampled.y) * attack.vx >= 0 ? 1 : -1;
    const angle = Math.atan2(-turn * attack.vx, turn * attack.vy) + turn * speed / attack.orbitRadiusM * elapsedS;
    return { x: centre.x + attack.orbitRadiusM * Math.cos(angle), y: centre.y + attack.orbitRadiusM * Math.sin(angle) };
  }

  /** Capture only attacks, not the entire world, before switching packet clocks. */
  private captureAttackPositions(now: number): Map<NetObjectId, Position> {
    const positions = new Map<NetObjectId, Position>();
    for (const [id, timed] of this.objects) {
      if (timed.current.kind === 'attack' && (timed.current.vx !== 0 || timed.current.vy !== 0)) {
        positions.set(id, this.attackPosition(timed, now));
      }
    }
    return positions;
  }

  /** Preserve browser-frame continuity at packet arrival, but never smear a major authoritative correction. */
  private smoothAttackCorrections(previous: Map<NetObjectId, Position>, now: number): void {
    for (const [id, oldPosition] of previous) {
      const timed = this.objects.get(id);
      if (timed?.current.kind !== 'attack' || (timed.current.vx === 0 && timed.current.vy === 0)) continue;
      timed.motionCorrection = undefined;
      const nextPosition = this.attackPosition(timed, now);
      const dx = oldPosition.x - nextPosition.x;
      const dy = oldPosition.y - nextPosition.y;
      const distance = Math.hypot(dx, dy);
      const maxCorrection = Math.max(this.actorRadiusM, Math.hypot(timed.current.vx, timed.current.vy) * 2 / this.tickRate);
      if (distance > 1e-9 && distance <= maxCorrection) {
        timed.motionCorrection = { offset: { x: dx, y: dy }, startedAtMs: now };
      }
    }
  }

  /** Implicit frames interpolate positions; an explicit tick preserves deterministic local prediction. */
  renderObjects(renderTick?: number, now = performance.now()): ObjectSnapshot[] {
    const tickDurationMs = 1_000 / this.tickRate;
    const elapsedTicks = Math.min(1, Math.max(0, (now - this.latestReceivedAtMs) / tickDurationMs));
    const presentationTick = renderTick ?? this.latestServerTick - this.interpolationDelayTicks + elapsedTicks;
    const monsterPresentationTick = renderTick ?? this.latestServerTick - this.monsterInterpolationDelayTicks + elapsedTicks;
    const result: ObjectSnapshot[] = [];
    for (const [id, timed] of this.objects) {
      if (id === this.playerNetId && this.predicted) {
        const local = cloneObject(this.predicted);
        // Explicit-tick consumers compare reconstructed simulation state. Only
        // real browser frames (implicit clock) should see wall-clock smoothing.
        const position = renderTick === undefined ? this.localPosition(now) : undefined;
        if (position) { local.x = position.x; local.y = position.y; }
        result.push(local);
        continue;
      }
      // Corrected/stateful attacks must retain exact authoritative transforms
      // for explicit-tick reconstruction and adversarial wire-parity checks.
      if (timed.current.kind === 'attack' && (renderTick === undefined || timed.current.replication === 'DETERMINISTIC')
        && (timed.current.vx !== 0 || timed.current.vy !== 0)) {
        if (renderTick === undefined) result.push({ ...timed.current, ...this.attackPosition(timed, now) });
        else {
          const elapsedS = Math.max(0, renderTick - timed.currentTick) / this.tickRate;
          result.push({ ...timed.current,
            x: timed.current.x + timed.current.vx * elapsedS,
            y: timed.current.y + timed.current.vy * elapsedS,
          });
        }
        continue;
      }
      if (timed.current.kind === 'attack' && timed.current.replication === 'DETERMINISTIC') {
        const elapsedS = Math.max(0, presentationTick - timed.currentTick) / this.tickRate;
        result.push({ ...timed.current, x: timed.current.x + timed.current.vx * elapsedS, y: timed.current.y + timed.current.vy * elapsedS });
        continue;
      }
      // Only transforms lag. Telegraphs, HP, statuses and other gameplay metadata
      // must always come from the newest accepted authoritative object.
      const displayed = cloneObject(timed.current);
      const position = this.delayedPosition(timed, displayed.kind === 'monster' ? monsterPresentationTick : presentationTick, renderTick === undefined);
      displayed.x = position.x;
      displayed.y = position.y;
      result.push(displayed);
    }
    return result.sort((a, b) => a.netId.localeCompare(b.netId));
  }

  /** Remote transforms interpolate buffered samples; explicit-tick replay retains the original transforms. */
  private delayedPosition(timed: TimedObject, presentationTick: number, easeDisplacement = false): Position {
    const samples = timed.samples;
    let position: Position = { x: samples[0]!.object.x, y: samples[0]!.object.y };
    for (let i = 1; i < samples.length; i++) {
      const previous = samples[i - 1]!;
      const next = samples[i]!;
      if (presentationTick <= previous.tick) break;
      const alpha = Math.min(1, (presentationTick - previous.tick) / (next.tick - previous.tick));
      position = lerpPosition(previous.object, next.object, alpha);
      if (alpha < 1) break;
    }
    if (!easeDisplacement || (timed.current.kind !== 'monster' && timed.current.kind !== 'player')) return position;
    // A skill can move an actor farther than locomotion permits in one sample interval. Spread only that
    // excess travel over a PROVISIONAL presentation interval, leaving state and explicit replay untouched.
    for (let i = 1; i < samples.length; i++) {
      const previous = samples[i - 1]!;
      const next = samples[i]!;
      const object = next.object;
      if (presentationTick <= previous.tick || (object.kind !== 'monster' && object.kind !== 'player')) continue;
      if (object.kind === 'player' && !next.skillDisplaced && object.moveSpeedMps === undefined) continue;
      const span = next.tick - previous.tick;
      const duration = Math.max(span, REMOTE_SKILL_DISPLACEMENT_EASE_MS * this.tickRate / 1000);
      if (presentationTick >= previous.tick + duration) continue;
      const before = previous.object;
      const speed = object.kind === 'monster' && before.kind === 'monster'
        ? Math.max(Math.hypot(object.vx, object.vy), Math.hypot(before.vx, before.vy))
        : Math.max(object.kind === 'player' ? object.moveSpeedMps ?? this.playerSpeedMps : 0, before.kind === 'player' ? before.moveSpeedMps ?? this.playerSpeedMps : 0);
      const dx = object.x - previous.object.x;
      const dy = object.y - previous.object.y;
      if (Math.hypot(dx, dy) <= speed * span / this.tickRate + 1e-6) continue;
      const normal = Math.min(1, speed * span / this.tickRate / Math.hypot(dx, dy));
      const t = Math.min(1, (presentationTick - previous.tick) / duration);
      const correction = t * t * (3 - 2 * t) - Math.min(1, (presentationTick - previous.tick) / span);
      position = { x: position.x + dx * (1 - normal) * correction, y: position.y + dy * (1 - normal) * correction };
    }
    return position;
  }

  private localPosition(now: number): Position | undefined {
    if (!this.localPresentation) return this.predicted ? { x: this.predicted.x, y: this.predicted.y } : undefined;
    const { from, to, startedAtMs, dashMs } = this.localPresentation;
    if (dashMs !== undefined) {
      const t = Math.max(0, Math.min(1, (now - startedAtMs) / dashMs));
      // Smoothstep: zero speed at both ends, fastest in the middle.
      return lerpPosition(from, to, t * t * (3 - 2 * t));
    }
    const elapsed = Math.max(0, Math.min(1, (now - startedAtMs) * this.tickRate / 1_000));
    return lerpPosition(from, to, elapsed);
  }

  /**
   * Whether the delta carries a skill displacement of the local player not yet accepted, in `(tick, seq)` order: the
   * authoritative position of that tick is then an accepted move, not a correction or a teleport.
   */
  private acceptSkillDisplacements(events: readonly PresentationEvent[]): boolean {
    let accepted = false;
    const own = events
      .filter((event): event is Extract<PresentationEvent, { type: 'skill_displacement' }> => event.type === 'skill_displacement' && event.sourceId === this.playerNetId)
      .sort((a, b) => a.tick - b.tick || a.seq - b.seq);
    for (const event of own) {
      const last = this.lastSkillDisplacement;
      if (last !== undefined && (event.tick < last.tick || (event.tick === last.tick && event.seq <= last.seq))) continue;
      this.lastSkillDisplacement = { tick: event.tick, seq: event.seq };
      accepted = true;
    }
    return accepted;
  }

  /**
   * `dashedM` is the distance a common dash moved the predicted player in the step or replay just made (0 without one).
   * `skillDisplaced`: the authoritative position includes a skill displacement just accepted; the drawn position eases to
   * it from where it is drawn now, over `SKILL_DISPLACEMENT_EASE_MS`, however far it is.
   */
  private updateLocalPresentation(from: Position | undefined, now: number, dashedM = 0, skillDisplaced = false): void {
    if (!this.predicted) { this.localPresentation = undefined; return; }
    const to = { x: this.predicted.x, y: this.predicted.y };
    if (!from) { this.localPresentation = { from: to, to, startedAtMs: now }; return; }
    if (skillDisplaced) { this.localPresentation = { from, to, startedAtMs: now, dashMs: SKILL_DISPLACEMENT_EASE_MS }; return; }
    const current = this.localPresentation;
    if (current?.to.x === to.x && current.to.y === to.y) return;
    // The farthest a correction is smoothed over; anything longer is a teleport or respawn and is never drawn as a slide.
    const maxSmoothDistance = Math.max(2, (this.predicted.moveSpeedMps ?? this.playerSpeedMps) * 3 / this.tickRate);
    if (current?.dashMs !== undefined && now < current.startedAtMs + current.dashMs) {
      // A dash still being drawn keeps its start and timing and follows the newest predicted position,
      // unless that position left the dash's own landing by more than a correction: then it snaps.
      const moved = Math.hypot(to.x - current.to.x, to.y - current.to.y);
      this.localPresentation = moved <= maxSmoothDistance ? { ...current, to } : { from: to, to, startedAtMs: now };
      return;
    }
    const jump = Math.hypot(to.x - from.x, to.y - from.y);
    // Only a jump the dash itself explains is drawn as one; a longer one is a teleport that happens to carry a replayed dash.
    if (dashedM > 0 && jump > maxSmoothDistance && jump <= dashedM + maxSmoothDistance) {
      this.localPresentation = { from, to, startedAtMs: now, dashMs: Math.max(DASH_PRESENTATION_MIN_MS, DASH_PRESENTATION_MS * dashedM / COMMON_DASH_MAX_DISTANCE_M) };
      return;
    }
    this.localPresentation = { from: jump > maxSmoothDistance ? to : from, to, startedAtMs: now };
  }

  private acceptObject(object: ObjectSnapshot, tick: number, skillDisplaced = false): void {
    const existing = this.objects.get(object.netId);
    const samples = [...(existing?.samples ?? []), { object: cloneObject(object), tick, skillDisplaced }];
    const maxSamples = Math.min(64, Math.max(4, Math.ceil(Math.max(this.interpolationDelayTicks, this.monsterInterpolationDelayTicks) + REMOTE_SKILL_DISPLACEMENT_EASE_MS * this.tickRate / 1000) + 3));
    if (samples.length > maxSamples) samples.splice(0, samples.length - maxSamples);
    this.objects.set(object.netId, { previous: existing?.current, current: cloneObject(object), previousTick: existing?.currentTick ?? tick, currentTick: tick, samples,
      launch: existing?.launch, motionCorrection: existing?.motionCorrection });
    if (object.kind === 'player' && object.netId === this.playerNetId) this.predicted = cloneObject(object);
  }

  /** Returns the metres a replayed, still unacknowledged common dash moved the predicted player (0 without one). */
  private reconcile(lastProcessed: number): number {
    const authoritative = this.objects.get(this.playerNetId)?.current;
    if (authoritative?.kind !== 'player') return 0;
    this.predicted = cloneObject(authoritative);
    while (this.inputHistory.length && this.inputHistory[0]!.sequence <= lastProcessed) this.inputHistory.shift();
    // Only one unacknowledged common dash can be predicted, regardless of
    // repeated command attempts. The server remains the cooldown authority.
    let priorDashPending = false;
    let dashedM = 0;
    for (const input of this.inputHistory) {
      dashedM += this.predictStep(input.moveX, input.moveY, input.dash, priorDashPending);
      if (input.dash !== undefined) priorDashPending = true;
    }
    return dashedM;
  }

  /** Returns the metres a predicted common dash moved the player this step (0 without one). */
  private predictStep(moveX: number, moveY: number, dash?: PendingDash, priorDashPending = false): number {
    if (!this.predicted || this.predicted.down || !this.predicted.connected || this.predicted.movementAllowed === false) return 0;
    let dashedM = 0;
    const length = Math.hypot(moveX, moveY); const scale = length > 1 ? 1 / length : 1;
    const axis = { x: moveX * scale, y: moveY * scale };
    // Common dash is executed before ordinary movement in AuthoritativeWorld.
    // A client without terrain or a server-ready cooldown cannot safely
    // predict it; authoritative snapshots always resolve the pending action.
    if (dash !== undefined && !priorDashPending && this.terrain !== undefined
        && (this.predicted.commonDashReadyTick === undefined
          ? this.predicted.commonDashCooldownTicks === 0
          : this.latestServerTick + 1 >= this.predicted.commonDashReadyTick)
        && (this.predicted.moveSpeedMps ?? this.playerSpeedMps) > 0) {
      const heading = Math.hypot(axis.x, axis.y) > 1e-9 ? axis : this.predicted.facing;
      if (heading) {
        const moved = resolveDashMovement(
          this.terrain, { x: this.predicted.x, y: this.predicted.y }, heading,
          dash.distanceM ?? COMMON_DASH_MAX_DISTANCE_M, COMMON_DASH_MAX_DISTANCE_M,
        );
        if (moved) { dashedM = Math.hypot(moved.landed.x - this.predicted.x, moved.landed.y - this.predicted.y); this.predicted.x = moved.landed.x; this.predicted.y = moved.landed.y; }
      }
    }
    if (Math.hypot(axis.x, axis.y) > 1e-9) this.predicted.facing = { ...axis };
    const speed = this.predicted.moveSpeedMps ?? this.playerSpeedMps;
    const delta = { x: axis.x * speed / this.tickRate, y: axis.y * speed / this.tickRate };
    const next = this.terrain === undefined
      ? { x: this.predicted.x + delta.x, y: this.predicted.y + delta.y }
      : sweepMovement(this.terrain, { x: this.predicted.x, y: this.predicted.y }, delta, this.actorRadiusM).position;
    this.predicted.x = next.x;
    this.predicted.y = next.y;
    return dashedM;
  }

  private storeBaseline(tick: number, objects: Map<NetObjectId, ObjectSnapshot>): void {
    this.baselines.set(tick, objects);
    for (const old of this.baselines.keys()) {
      if (old === this.pinnedBaselineTick) continue;
      if (old < tick - CLIENT_BASELINE_RETENTION_TICKS) this.baselines.delete(old);
    }
  }

  /** Diagnostic: retained baseline count, bounded by the rolling window plus at most one pin. */
  retainedBaselineCount(): number { return this.baselines.size; }
}
